import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Col, Input, Label, Row } from "reactstrap";
import UploadBook from "./UploadBook";
import UploadWritingSample from "./UploadWritingSample";

// Import constants
import { FELLOWSHIP_THEMES, FELLOWSHIP_PROGRAMS } from '../../constants/fellowshipThemes';

const FellowshipInformation = forwardRef(
  ({ applicationId, onSave, onNext, data, onDataChange }, ref) => {
    const [formData, setFormData] = useState({
      programAppliedFor: "",
      themes: {},
      othersField: "",
      bookProposal: null,
      writingSample: null,
    });

    const [errors, setErrors] = useState({});
    const [showValidation, setShowValidation] = useState(false);
    const [themes, setThemes] = useState([]);
    const [loadingThemes, setLoadingThemes] = useState(true);
    const [themesError, setThemesError] = useState(null);

    // Fellowship program options from constants
    const fellowshipPrograms = FELLOWSHIP_PROGRAMS;

    // Use predefined themes from constants
    useEffect(() => {
      const initializeThemes = () => {
        try {
          setLoadingThemes(true);
          setThemesError(null);

          setThemes(FELLOWSHIP_THEMES);

          // Initialize themes object with fetched themes
          const initialThemes = {};
          FELLOWSHIP_THEMES.forEach((key) => {
            initialThemes[key] = false;
          });
          initialThemes.others = false; // Always include 'others' option

          setFormData((prev) => ({
            ...prev,
            themes: initialThemes,
          }));
        } catch (error) {
          console.error("Error initializing themes:", error);
          setThemesError(
            "Failed to initialize themes. Please refresh the page."
          );
        } finally {
          setLoadingThemes(false);
        }
      };

      initializeThemes();
    }, []);
    

    // Load saved data when component mounts or data changes
    useEffect(() => {
      if (data) {
        setFormData((prevData) => {
          let updatedThemes = prevData.themes;
          if (data.themes && typeof data.themes === "object") {
            updatedThemes = { ...prevData.themes, ...data.themes };
          }
          // If othersField is present, check the 'others' checkbox
          if (data.othersField && data.othersField.trim()) {
            updatedThemes = { ...updatedThemes, others: true };
          }
          const updatedData = {
            ...prevData,
            ...data,
            themes: updatedThemes,
          };
          return updatedData;
        });
      }
    }, [data]);

    // Count selected themes
    const selectedThemesCount = Object.values(formData.themes).filter(
      Boolean
    ).length;

    const handleChange = (e) => {
      const { name, value, type, checked } = e.target;
      console.log("Handling change for:", name, value, type, checked);

      if (type === "checkbox") {
        // Handle theme selection with max 3 limit
        if (name === "others" || "Others") {
          setFormData((prev) => {
            const updatedThemes = {
              ...prev.themes,
              [name]: checked,
            };
            const updatedData = {
              ...prev,
              themes: updatedThemes,
            };


            console.log("Updated themes:", updatedThemes);

            onDataChange(updatedData);
            return updatedData;
          });
        } else {
          // For other themes, check if we're at max limit
          const currentCount = Object.values(formData.themes).filter(
            Boolean
          ).length;
          if (checked && currentCount >= 3 && !formData.themes[name]) {
            // Don't allow more than 3 selections
            return;
          }

          setFormData((prev) => {
            const updatedThemes = {
              ...prev.themes,
              [name]: checked,
            };
            const updatedData = {
              ...prev,
              themes: updatedThemes,
            };
            onDataChange(updatedData);
            return updatedData;
          });
        }
      } else {
        setFormData((prev) => {
          const updatedData = {
            ...prev,
            [name]: value,
          };
          onDataChange(updatedData);
          return updatedData;
        });
      }
    };

    const handleThemeToggle = (key) => {
      const currentCount = Object.values(formData.themes).filter(
        Boolean
      ).length;
      
      const isCurrentlySelected = formData.themes[key];

      if (!isCurrentlySelected && currentCount >= 3) {
        return;
      }

      setFormData((prev) => {
        const updatedThemes = {
          ...prev.themes,
          [key]: !prev.themes[key],
        };
        const updatedData = {
          ...prev,
          themes: updatedThemes,
        };
        onDataChange(updatedData);
        return updatedData;
      });
    };

    const handleFileSelect = (type, file) => {
      setFormData((prev) => {
        const updatedData = {
          ...prev,
          [type]: file,
        };
        onDataChange(updatedData);
        return updatedData;
      });
    };

    // Validation function
    const validate = () => {
      const newErrors = {};

      if (!formData.programAppliedFor) {
        newErrors.programAppliedFor = "Program Applied For is required";
      }

      if (selectedThemesCount === 0) {
        newErrors.themes = "Please select at least one theme";
      }

      if (selectedThemesCount > 3) {
        newErrors.themes = "You can select up to 3 themes";
      }

      if (formData.themes.others && !formData.othersField.trim()) {
        newErrors.othersField = "Please specify the other theme";
      }

      if (!formData.bookProposal) {
        newErrors.bookProposal = "Book Proposal is required";
      }

      if (!formData.writingSample) {
        newErrors.writingSample = "Writing Sample is required";
      }

      setErrors(newErrors);
      setShowValidation(true);
      return Object.keys(newErrors).length === 0;
    };

    // Handle next with validation
    const handleNextWithValidation = () => {
      if (validate()) {
        onNext();
      }
    };

    // Handle save with validation
    const handleSaveWithValidation = () => {
      if (validate()) {
        onSave();
      }
    };

    // Define formDataToSend as a FormData instance
    const formDataToSend = new FormData();

    // Updated logic for handling selected themes
    let selectedThemes = Object.entries(formData.themes)
      .filter(([key, value]) => value)
      .map(([key]) => key);

    // If 'others' is selected, replace it with the user's input only if valid
    if (selectedThemes.includes("others")) {
      const customTheme = formData.othersField.trim();
      if (customTheme && !themes.includes(customTheme) && customTheme.toLowerCase() !== "others") {
        selectedThemes = selectedThemes.map(t => t === "others" ? "others" : t);
      } else {
        // Remove 'others' if input is empty or matches a predefined theme or is literally 'others'
        selectedThemes = selectedThemes.filter(t => t !== "others");
      }
    }

    // Append themes only if there are valid selections
    if (selectedThemes.length > 0) {
      formDataToSend.append(
        "fellowship_themes",
        JSON.stringify(selectedThemes)
      );
    } else {
      console.warn(
        "No valid themes selected. fellowship_themes will not be appended."
      );
    }



    // Append the "othersField" separately if it exists
    if (formData.themes.others && formData.othersField.trim()) {
      formDataToSend.append(
        "fellowship_othersField",
        formData.othersField.trim()
      );
    }
    useImperativeHandle(ref, () => ({
      handleSaveWithValidation,
      handleNextWithValidation,
      validate, // Expose validate method
      getFormData: () => formData, // Expose form data
    }));

    return (
      <>
        <Row>
          <Col xl="12">
            <h2 className="personalinfo_head">Fellowship Information</h2>
          </Col>
        </Row>

        <div className="py-4">
          <Row>
            <Col xl="12">
              <div className="personal_formdetail">
                <h5>
                  Program Applied For <span className="text-danger">*</span>
                </h5>
                <select
                  className={`select_gender${
                    showValidation && errors.programAppliedFor
                      ? " is-invalid"
                      : ""
                  }`}
                  name="programAppliedFor"
                  value={formData.programAppliedFor}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Program</option>
                  {fellowshipPrograms.map((program, index) => (
                    <option key={index} value={program}>
                      {program}
                    </option>
                  ))}
                </select>
                {showValidation && errors.programAppliedFor && (
                  <div className="invalid-feedback d-block">
                    {errors.programAppliedFor}
                  </div>
                )}
              </div>
            </Col>

            <Col xl="12">
              <div className="personal_formdetail">
                <h5>Indicative Themes</h5>
                <p className="text-muted">You can select up to 3 themes.</p>
                {showValidation && errors.themes && (
                  <div className="text-danger mb-2">{errors.themes}</div>
                )}
              </div>
            </Col>

            <Col xl="12">
              {loadingThemes ? (
                <div className="text-center py-3">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading themes...</span>
                  </div>
                  <p className="mt-2">Loading themes...</p>
                </div>
              ) : themesError ? (
                <div className="alert alert-danger">
                  {themesError}
                  <button
                    className="btn btn-sm btn-outline-danger ms-2"
                    onClick={() => window.location.reload()}
                  >
                    Refresh Page
                  </button>
                </div>
              ) : (
                <ul className="fello_list">
                  {themes.map((theme) => (
                    <li key={theme}>
                      <div className="rem_input custom-checkbox custom-control">
                        <Input
                          type="checkbox"
                          id={`theme-${theme}`}
                          name={theme}
                          className="custom-control-input"
                          checked={formData.themes[theme] || false}
                          onChange={() => handleThemeToggle(theme)}
                          disabled={
                            !formData.themes[theme] && selectedThemesCount >= 3
                          }
                        />
                        <Label
                          className="custom-control-label"
                          htmlFor={`theme-${theme}`}
                        >
                          {theme}
                        </Label>
                      </div>
                    </li>
                  ))}
                  <li>
                    <div className="rem_input custom-checkbox custom-control">
                      <Input
                        type="checkbox"
                        id="theme-others"
                        name="others"
                        className="custom-control-input"
                        checked={formData.others || formData.themes.others || false}
                        onChange={() => {
                          // If unchecking, also clear othersField
                          if (formData.themes.others) {
                            setFormData((prev) => {
                              const updatedData = {
                                ...prev,
                                themes: { ...prev.themes, others: false },
                                othersField: "",
                              };
                              onDataChange(updatedData);
                              return updatedData;
                            });
                          } else {
                            handleThemeToggle("others");
                          }
                        }}
                        disabled={
                          !formData.themes.others && selectedThemesCount >= 3
                        }
                      />
                      <Label
                        className="custom-control-label"
                        htmlFor="theme-others"
                      >
                        Others
                      </Label>
                    </div>
                    {formData.themes.others && (
                      <div className="personal_formdetail mt-2">
                        <label>Others Field</label>
                        <input
                          type="text"
                          name="othersField"
                          value={formData.othersField}
                          required
                          // onChange={handleChange}
                          onChange={(e) => {
                            setFormData((prev) => {
                              const updatedData = {
                                ...prev,
                                othersField: e.target.value,
                              };                              
                              onDataChange(updatedData);
                              return updatedData;
                            });
                          }}
                          className="form-control form_custom"
                          placeholder="Please specify the other theme (max 100 characters)"
                          maxLength={100}
                        />
                        {showValidation && errors.othersField && (
                          <div className="text-danger mb-2">{errors.othersField}</div>
                        )}
                        <small className="text-muted">
                          {formData.othersField.length}/100 characters
                        </small>
                      </div>
                    )}
                  </li>
                </ul>
              )}
            </Col>
          </Row>

          <Row>
            <Col xl="12">
              <div className="personal_formdetail">
                <h5>
                  Upload Book Proposal <span className="text-danger">*</span>
                </h5>
                <p className="text-muted">PDF format, Max 5MB</p>
                {showValidation && errors.bookProposal && (
                  <div className="text-danger mb-2">{errors.bookProposal}</div>
                )}
                <UploadBook
                  onFileSelect={(file) =>
                    handleFileSelect("bookProposal", file)
                  }
                  initialFile={formData.bookProposal}
                />
              </div>
            </Col>
          </Row>

          <Row className="mt-4">
            <Col xl="12">
              <div className="personal_formdetail">
                <h5>
                  Upload Writing Sample <span className="text-danger">*</span>
                </h5>
                <p className="text-muted">PDF format, Max 5MB</p>
                {showValidation && errors.writingSample && (
                  <div className="text-danger mb-2">{errors.writingSample}</div>
                )}
                <UploadWritingSample
                  onFileSelect={(file) =>
                    handleFileSelect("writingSample", file)
                  }
                  initialFile={formData.writingSample}
                />
              </div>
            </Col>
          </Row>

          <Row className="mt-4">
            <Col xl="12" className="d-flex justify-content-between">
              {/* Remove extra navigation buttons here */}
            </Col>
          </Row>
        </div>
      </>
    );
  }
);

export default FellowshipInformation;
